import { Component, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import {NavigationEnd, Router} from '@angular/router'
import {filter} from 'rxjs'
import {environment} from '../../../environments/environment'
import {AuthService} from '../../services/auth.service'
import {SidebarService, SideBarItem} from '../../services/sidebar.service'
import {MeService} from '@libs/auth'

/**
 * Argus's left sidebar — Juno's, with Argus's items.
 *
 * Deliberately the same structure as `projects/juno/.../sidebar`:
 * collapsible via the header icon, Material Symbols glyphs, one flat item list,
 * a footer strip. Two internal apps in one workspace should not have two
 * different navs.
 *
 * WHERE IT DIFFERS, AND WHY: Juno's footer is a DEV/PROD switcher backed by
 * its `MigrateEnvironment`, which re-points the API base at runtime. Argus has
 * no such mechanism — one `baseUrl` per build — so the footer here REPORTS the
 * build instead of offering a choice. That is not a downgrade: the booking
 * console can create and delete real appointments, so "which backend am I
 * pointed at" is a fact the tester must be able to read at a glance, and a
 * production build says so in amber.
 *
 * It also owns SIGN OUT. That briefly sat in a top bar, which was one piece of
 * chrome too many: Juno has no top bar, both Argus pages are dense data
 * screens that want the vertical space, and a nav-shaped shell is where a
 * sign-out belongs. Juno has no explicit sign-out at all — read as a gap
 * there, not a pattern to copy, since a QA tool exists to switch accounts.
 */
@Component({
  selector: 'app-sidebar',
  imports: [],
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly sidebarService = inject(SidebarService);
  private readonly authService = inject(AuthService);
  private readonly meService = inject(MeService);
  /** the way into My account, once argus signs in as an employee and configures mePath */
  readonly myAccount = this.meService.enabled

  readonly sideBarItems = this.sidebarService.getSideBarItems()
  readonly showNav = signal(true)
  /** The URL owns the highlight. A signal, so a router event re-renders this view under OnPush. */
  private readonly activePath = signal('')

  readonly envName = environment.envName ?? 'development'
  readonly backendHost = environment.baseUrl ? new URL(environment.baseUrl).host : 'local dev proxy'
  /** Production builds reach live salons; the footer says so loudly. */
  readonly isProd = this.envName === 'production'

  ngOnInit(): void {
    this.markSelected(this.router.url)
    // Deep links and browser back must light the right item, so follow the
    // router rather than only the click.
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.markSelected(e.urlAfterRedirects || e.url))
  }

  private markSelected(url: string): void {
    // Match on the PATH alone: /turn-demo carries its mode and date as query
    // params (?mode=&date=), and a matcher that compares the full URL would
    // un-highlight the item the moment a param lands.
    this.activePath.set(url.split('?')[0])
  }

  openMyAccount(): void {
    this.router.navigate(['/my-account'])
  }

  isSelected(item: SideBarItem): boolean {
    const path = this.activePath()
    return path === item.path || path.startsWith(`${item.path}/`)
  }

  selectSideBarItem(item: SideBarItem): void {
    this.router.navigate([item.path])
  }

  toggleShowNav(): void {
    this.showNav.update(v => !v)
  }

  navigateToHomePage(): void {
    this.router.navigate(['/customers'])
  }

  /** Collapsed, the footer has room for a tag, not a hostname. */
  get envTag(): string {
    return this.envName.slice(0, 4).toUpperCase()
  }

  logOut(): void {
    this.router.navigate(['/']).then(() => window.location.reload())
    this.authService.logOut()
  }
}
